import { SmsVoiceNumberFilters } from '@reliance/types/filters/SmsVoiceNumberFilters';
import { SmsVoiceNumber } from '@reliance/types/models/SmsVoiceNumber';
import { Inmate } from '@reliance/types/models/Inmate';
import { createColumnHelper, Row } from '@tanstack/react-table';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { formatPhoneNumber } from '../../format/PhoneNumber';
import { smsVoiceNumberViewPath } from '../../sms-voice-number/paths';
import { useSmsVoiceNumbersByInmateId } from '../../hooks/SmsVoiceNumber';
import { useTabLazyLoad } from '../../hooks/useTabLazyLoad';
import { useNotifyTabLoading } from '../../hooks/useTabLoadingReporter';
import BaseHtmlTable from '../../table/BaseHtmlTable';
import { useServerTableFilterState } from '../../table/useServerTableFilterState';
import { formatDate } from '../../format/DateTime';

type InmateSmsVoiceNumbersProps = {
  inmate: Inmate;
  tabSelected?: boolean;
  refreshTrigger?: number;
};

const DEFAULT_PER_PAGE = 100;
const DEFAULT_ORDER_BY = 'number';

const COLUMNS = [
  { id: 'id', meta: { sortName: 'id' } },
  { id: 'datePurchased', meta: { sortName: 'datePurchased' } },
  { id: 'dateReturned', meta: { sortName: 'dateReturned' } },
  { id: 'provider', meta: { sortName: 'provider' } },
  { id: 'number', meta: { sortName: 'number' } },
  { id: 'region', meta: { sortName: 'region' } },
];

const InmateSmsVoiceNumbers: React.FC<InmateSmsVoiceNumbersProps> = (props) => {
  const { inmate, tabSelected, refreshTrigger } = props;

  const baseRecord = useMemo(() => ({ orderBy: DEFAULT_ORDER_BY }) as Record<string, unknown>, []);
  const tableState = useServerTableFilterState(COLUMNS, baseRecord, {
    initialPagination: { pageIndex: 0, pageSize: DEFAULT_PER_PAGE },
    initialSorting: [{ id: 'number', desc: false }],
  });

  const filters: SmsVoiceNumberFilters = useMemo(() => {
    const m = tableState.mergedRecord;
    const numberVal = m.number as string | undefined;
    return {
      page: m.page as number,
      perPage: m.perPage as number,
      orderBy: (m.orderBy as string | undefined) ?? DEFAULT_ORDER_BY,
      ...(numberVal != null && numberVal !== '' ? { number: numberVal } : {}),
    };
  }, [tableState.mergedRecord]);

  const numbersResult = useSmsVoiceNumbersByInmateId(null, filters);

  useTabLazyLoad({
    key: inmate.id,
    tabSelected,
    refreshTrigger,
    execute: useCallback(() => {
      if (inmate.id) {
        void numbersResult.execute(inmate.id);
      }
    }, [numbersResult, inmate.id]),
  });

  useEffect(() => {
    if (
      !tabSelected ||
      !inmate.id ||
      numbersResult.result === undefined ||
      numbersResult.loading
    ) {
      return;
    }
    void numbersResult.execute(inmate.id);
  }, [tableState.mergedRecord]);

  useNotifyTabLoading('numbers', numbersResult.loading);

  const data = numbersResult.result?.data ?? [];
  const total = numbersResult.result?.total ?? 0;
  const perPage = numbersResult.result?.perPage ?? tableState.pagination.pageSize;
  const totalPages = Math.ceil(total / perPage) || 1;
  const page = numbersResult.result?.page ?? tableState.pagination.pageIndex + 1;

  const columnHelper = createColumnHelper<SmsVoiceNumber>();

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.id, {
        id: 'id',
        header: 'ID',
        meta: { sortName: 'id' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.datePurchased, {
        id: 'datePurchased',
        header: 'Purchased',
        cell: (info) => formatDate(info.getValue()),
        meta: { sortName: 'datePurchased' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.dateReturned, {
        id: 'dateReturned',
        header: 'Returned',
        cell: (info) => formatDate(info.getValue()),
        meta: { sortName: 'dateReturned' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.provider, {
        id: 'provider',
        header: 'Provider',
        meta: { sortName: 'provider' },
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.number, {
        id: 'number',
        header: 'ANI',
        cell: (info) => {
          const value = info.getValue();
          if (!value) {
            return '';
          }
          const formatted = formatPhoneNumber(value);
          return (
            <Link to={smsVoiceNumberViewPath(value)}>{formatted}</Link>
          );
        },
        meta: { sortName: 'number' },
      }),
      columnHelper.accessor((row) => row.region, {
        id: 'region',
        header: 'Region',
        meta: { sortName: 'region' },
        enableColumnFilter: false,
      }),
    ],
    [columnHelper]
  );

  return (
    <div className="jarviswidget jarviswidget-color-blueDark">
      <div>
        <BaseHtmlTable
          rows={data}
          columnDefs={columns}
          page={page}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          serverPagination={tableState.serverPagination}
          onServerPaginationChange={tableState.onServerPaginationChange}
          serverSorting={tableState.serverSorting}
          onServerSortingChange={tableState.onServerSortingChange}
          serverColumnFilters={tableState.serverColumnFilters}
          onServerColumnFiltersChange={tableState.onServerColumnFiltersChange}
          paginationMode="server"
          loading={numbersResult.loading}
          pageSizeOptions={[100, 500, 1000]}
          isRowExpanded={(_row: Row<SmsVoiceNumber>) => false}
        />
      </div>
    </div>
  );
};

export default InmateSmsVoiceNumbers;
